import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "@/plugins/axios";
import { showMessage } from "@/utils/notifications";

export const useMfaStore = defineStore("mfa", () => {
  // State
  const mfaBarCodeImage = ref(null);
  const recoveryCodes = ref(null);

  // Getters
  const getMfaBarCodeImage = computed(() => mfaBarCodeImage.value);
  const getRecoveryCodes = computed(() => recoveryCodes.value);

  // Actions
  const setMfaBarCodeImage = (data) => {
    mfaBarCodeImage.value = data?.image || null;
  };

  const setRecoveryCodes = (data) => {
    recoveryCodes.value = data?.codes || null;
  };

  const setupMfaUser = (data) => {
    let user = JSON.parse(localStorage.getItem("breton-userInfo"));
    user["mfa_enabled"] = data.mfa_enabled;
    user["mfa_verified"] = true;
    localStorage.setItem("breton-userInfo", JSON.stringify(user));
  };

  // API Actions
  const getMFABarCodeImage = async (payload) => {
    try {
      const response = await axios.post("/qrcode", {
        password: payload.password,
        login_type: payload.login_type,
      });

      const resData = response.data;
      if (resData && !resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      } else {
        setMfaBarCodeImage(resData);
        return resData;
      }
    } catch (error) {
      console.error("Error getting MFA bar code image:", error);
      return { success: false, message: "Failed to get MFA bar code image" };
    }
  };

  const verifyMFASetupOtp = async (payload) => {
    try {
      const response = await axios.post("/verify_otp", {
        otp: payload.otp,
      });

      const resData = response.data;
      if (resData && !resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      } else {
        setRecoveryCodes({ codes: resData.data.codes });
        return response;
      }
    } catch (error) {
      console.error("Error verifying MFA setup OTP:", error);
      return { success: false, message: "Failed to verify MFA setup OTP" };
    }
  };

  const setupMFA = async (payload) => {
    try {
      const response = await axios.post("/setup_mfa", {
        mfa_enabled: payload.mfa_enabled,
        otp: payload.otp,
      });

      const resData = response.data;
      if (resData && !resData.success) {
        return { success: false, message: resData.message };
      } else {
        // Update current user in auth store
        const { useAuthStore } = await import("./auth");
        const authStore = useAuthStore();

        let currentUser = JSON.parse(JSON.stringify(authStore.currentUser));
        currentUser.mfa_enabled = payload.mfa_enabled;
        currentUser.mfa_verified = payload.mfa_enabled ? true : false;

        authStore.setState("currentUser", currentUser);
        setupMfaUser({ mfa_enabled: payload.mfa_enabled });

        let status = payload.mfa_enabled ? "activated" : "deactivated";

        localStorage.setItem("breton-userInfo", JSON.stringify(currentUser));

        showMessage({
          message: `Multi-factor Authentication has been ${status} successfully`,
          messageType: "success",
          messageTitle: "Success",
        });

        return response;
      }
    } catch (error) {
      console.error("Error setting up MFA:", error);
      return { success: false, message: "Failed to setup MFA" };
    }
  };

  const getRecoveryCodesAction = async () => {
    try {
      const response = await axios.get("/recovery_codes", {});
      const resData = response.data;

      if (!resData.success) {
        return { success: false, message: resData.message };
      } else {
        setRecoveryCodes({ codes: resData.data });
        return resData;
      }
    } catch (error) {
      console.error("Error getting recovery codes:", error);
      return { success: false, message: "Failed to get recovery codes" };
    }
  };

  const generateNewRecoveryCodes = async () => {
    try {
      const response = await axios.post("/recovery_codes", {});
      const resData = response.data;

      if (!resData.success) {
        return { success: false, message: resData.message };
      } else {
        setRecoveryCodes({ codes: resData.data });
        return resData;
      }
    } catch (error) {
      console.error("Error generating new recovery codes:", error);
      return { success: false, message: "Failed to generate new recovery codes" };
    }
  };

  return {
    // State
    mfaBarCodeImage,
    recoveryCodes,

    // Getters
    getMfaBarCodeImage,
    getRecoveryCodes,

    // Actions
    setMfaBarCodeImage,
    setRecoveryCodes,
    setupMfaUser,

    // API Actions
    getMFABarCodeImage,
    verifyMFASetupOtp,
    setupMFA,
    getRecoveryCodesAction,
    generateNewRecoveryCodes,
  };
});
